import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { HypothesesTracker } from '@/components/dashboard/HypothesesTracker';
import { DriftAlerts } from '@/components/dashboard/DriftAlerts';
import { useActionTasks, ActionTask, TaskStatus } from '@/hooks/useActionTasks';
import { useHypotheses } from '@/hooks/useHypotheses';
import { cn } from '@/lib/utils';
import {
  ListTodo,
  Lightbulb,
  ArrowUpRight,
  AlertCircle,
  Clock,
  CircleDot,
  CheckCircle2,
  Ban,
} from 'lucide-react';
import { formatDistanceToNow, isPast } from 'date-fns';
import { de } from 'date-fns/locale';

interface ExecutionTrackerProps {
  workspaceId: string | null;
  isLoading: boolean;
}

const PRIORITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const PRIORITY_STYLES: Record<string, string> = {
  critical: 'text-destructive border-destructive/30 bg-destructive/10',
  high: 'text-orange-600 dark:text-orange-400 border-orange-300/30 bg-orange-500/10',
  medium: 'text-muted-foreground border-border bg-muted/30',
  low: 'text-muted-foreground/60 border-border/50 bg-muted/20',
};

const STATUS_ICONS: Record<TaskStatus, React.ElementType> = {
  open: CircleDot,
  in_progress: Clock,
  done: CheckCircle2,
  blocked: Ban,
  cancelled: Ban,
};

export function ExecutionTracker({ workspaceId, isLoading }: ExecutionTrackerProps) {
  const navigate = useNavigate();
  const { tasks, updateTask } = useActionTasks(workspaceId || undefined);
  const { hypotheses } = useHypotheses(workspaceId || undefined);
  const [activeTab, setActiveTab] = useState('tasks');

  const openTasks = tasks.filter(t => t.status !== 'done' && t.status !== 'cancelled');
  const openHypotheses = hypotheses.filter(h => h.status === 'pending' || h.status === 'in_progress');

  // Sort by priority then due_date
  const sortedTasks = [...openTasks]
    .sort((a, b) => {
      const pA = PRIORITY_ORDER[a.priority] ?? 2;
      const pB = PRIORITY_ORDER[b.priority] ?? 2;
      if (pA !== pB) return pA - pB;
      if (a.due_date && b.due_date) return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      return 0;
    })
    .slice(0, 5);

  const handleToggleDone = (task: ActionTask) => {
    const newStatus: TaskStatus = task.status === 'done' ? 'open' : 'done';
    updateTask({ id: task.id, status: newStatus });
  };

  const handleCycleStatus = (task: ActionTask) => {
    const cycle: TaskStatus[] = ['open', 'in_progress', 'done'];
    const currentIdx = cycle.indexOf(task.status);
    const nextStatus = cycle[(currentIdx + 1) % cycle.length];
    updateTask({ id: task.id, status: nextStatus });
  };

  if (isLoading) {
    return (
      <div className="rounded-xl sm:rounded-2xl border border-border/50 bg-card p-4 sm:p-6">
        <div className="flex gap-2 mb-4">
          {[1, 2].map(i => (
            <div key={i} className="skeleton-shimmer h-9 flex-1 rounded-lg" />
          ))}
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton-shimmer h-14 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Don't render if nothing to show
  if (tasks.length === 0 && hypotheses.length === 0) return null;

  return (
    <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border border-border/50 bg-card transition-all duration-500 hover:border-primary/20">
      <div className="absolute top-0 right-0 w-32 sm:w-40 h-32 sm:h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="relative z-10">
        <div className="p-3 sm:p-4 border-b border-border/50">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                <ListTodo className="w-3.5 h-3.5 text-primary" />
              </div>
              Execution
            </h3>
          </div>
          <TabsList className="w-full grid grid-cols-2 bg-muted/30 h-9 sm:h-10">
            <TabsTrigger
              value="tasks"
              className="gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs sm:text-sm"
            >
              <ListTodo className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>Tasks</span>
              {openTasks.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[10px] bg-primary/10 text-primary">
                  {openTasks.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="hypotheses"
              className="gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs sm:text-sm"
            >
              <Lightbulb className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>Hypothesen</span>
              {openHypotheses.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[10px] bg-primary/10 text-primary">
                  {openHypotheses.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="tasks" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <div className="p-4 sm:p-6 space-y-3">
            {/* Drift Alerts */}
            {workspaceId && <DriftAlerts workspaceId={workspaceId} />}
            {sortedTasks.length === 0 ? (
              <div className="text-center py-8">
                <ListTodo className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  {tasks.length === 0
                    ? 'Noch keine Tasks vorhanden. Tasks können aus dem Report importiert werden.'
                    : 'Alle Tasks erledigt! 🎉'}
                </p>
              </div>
            ) : (
              sortedTasks.map((task) => {
                const isOverdue = task.due_date && isPast(new Date(task.due_date)) && task.status !== 'done';
                const StatusIcon = STATUS_ICONS[task.status] || CircleDot;
                return (
                  <div
                    key={task.id}
                    className={cn(
                      'group flex items-start gap-3 p-3 rounded-xl border transition-all duration-200',
                      'hover:border-primary/20 hover:bg-gradient-to-br hover:from-primary/5 hover:to-transparent',
                      isOverdue
                        ? 'border-destructive/30 bg-destructive/5'
                        : 'border-border/50 bg-background/50'
                    )}
                  >
                    <Checkbox
                      checked={task.status === 'done'}
                      onCheckedChange={() => handleToggleDone(task)}
                      className="mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'text-sm font-medium leading-relaxed',
                          task.status === 'done' && 'line-through text-muted-foreground'
                        )}
                      >
                        {task.title}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {task.session?.summary && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 max-w-[140px] truncate">
                            {task.session.summary}
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className={cn('text-[10px] px-1.5 py-0 h-5', PRIORITY_STYLES[task.priority])}
                        >
                          {task.priority}
                        </Badge>
                        {task.due_date && (
                          <span className={cn('text-[10px]', isOverdue ? 'text-destructive font-medium' : 'text-muted-foreground')}>
                            {isOverdue && <AlertCircle className="inline h-3 w-3 mr-0.5" />}
                            {formatDistanceToNow(new Date(task.due_date), { addSuffix: true, locale: de })}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 hover:bg-primary/10"
                      onClick={() => handleCycleStatus(task)}
                      title={`Status: ${task.status}`}
                    >
                      <StatusIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                );
              })
            )}

            {tasks.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/app/tasks')}
                className="w-full text-xs hover:bg-primary/5 hover:text-primary"
              >
                Alle Tasks anzeigen
                <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
          </div>
        </TabsContent>

        <TabsContent value="hypotheses" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <HypothesesTracker workspaceId={workspaceId || undefined} embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
